/**
 * Helper utilities for the EditCohortDetailsModal
 * Date parsing, formatting, and AM/PM toggle logic
 */

import type { SessionDateUpdate } from '../../../../services/api/cohorts';
import { toISODateString } from '../../../../utils/dateHelpers';
import type { SessionDateRow } from './editCohortTypes';

/** Generate unique ID for new rows */
export const generateId = () => Math.random().toString(36).substring(7);

/**
 * Parse datetime string to Date object
 * For datetimes, we want to preserve the actual time, not just the date
 */
export function parseDateTime(dateTime: string | null | undefined): Date | null {
  if (!dateTime) return null;
  return new Date(dateTime);
}

/**
 * Format Date to normalized comparison string for change detection
 * Uses UTC to ensure consistent comparison regardless of timezone
 */
export function toNormalizedDateString(date: Date | null | undefined): string | null {
  if (!date) return null;
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    return dateObj.toISOString().split('T')[0];
  } catch {
    return null;
  }
}

/** Format Date to ISO string (datetime) */
export function toISODateTimeString(date: Date | null | undefined): string | null {
  if (!date) return null;
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    return dateObj.toISOString();
  } catch {
    return null;
  }
}


/**
 * MED-1360: the session dates to save, or an empty list when no session date
 * differs from the loaded value. The sessions endpoint recalculates task due
 * dates, so a save that changes no session date must not call it.
 */
export function changedSessionUpdates(
  current: SessionDateRow[],
  original: SessionDateRow[]
): SessionDateUpdate[] {
  const originalDates = new Map(original.map(s => [s.id, toISODateString(s.date)]));
  const changed =
    current.length !== original.length ||
    current.some(
      s => !originalDates.has(s.id) || originalDates.get(s.id) !== toISODateString(s.date)
    );
  if (!changed) return [];
  return current
    .filter(s => toISODateString(s.date) !== undefined)
    .map(s => ({
      session_number: s.sessionNumber,
      scheduled_date: toISODateString(s.date)!,
    }));
}
